/**
 * All-Time Feature Helpers
 *
 * Pure utility functions and constants for the "All Time" date range feature.
 * The feature allows users to view data from their first transaction to today.
 *
 * URL Pattern: /all/:interval/to-date
 * Example: /@company/home/all/month/to-date
 *
 */

/*
 CONSTANTS
*/

export const ALL_TIME_YEAR_TOKEN = "all"
export const ALL_TIME_INDEX_TOKEN = "to-date"

/**
 * Regex pattern to match all-time URL paths.
 * Matches: /all/month/to-date, /all/quarter/to-date, etc.
 */
export const ALL_TIME_URL_PATTERN = new RegExp(
  `/${ALL_TIME_YEAR_TOKEN}/\\w+/${ALL_TIME_INDEX_TOKEN}(?:/|$)`
)

/*
 HELPERS
*/

interface RouteParams {
  year?: string | number
  index?: string | number
}

interface AllTimeUrlParams extends Record<string, string> {
  year: string
  interval: string
  index: string
}

/**
 * Generates URL params for all-time routes.
 * Converts the interval to lowercase for URL format.
 *
 * @param interval - The interval (e.g., "Month", "Quarter", "Year")
 * @returns URL params object with all-time tokens
 */
export function allTimeUrlParams(interval: string): AllTimeUrlParams {
  return {
    year: ALL_TIME_YEAR_TOKEN,
    interval: interval.toLowerCase(),
    index: ALL_TIME_INDEX_TOKEN,
  }
}

/**
 * Checks if route params represent the all-time URL pattern.
 * Use this when you have parsed URL params (year, index).
 */
export function isAllTimeRouteParams(params: RouteParams): boolean {
  return params.year === ALL_TIME_YEAR_TOKEN && params.index === ALL_TIME_INDEX_TOKEN
}

/**
 * Checks if a URL pathname matches the all-time pattern.
 * Use this when you have a raw pathname string.
 */
export function isAllTimeUrlPath(pathname: string): boolean {
  return ALL_TIME_URL_PATTERN.test(pathname)
}
